import { useGetAttachmentsByDocId } from "@/api/attachment";
import {
  getRelativePosition,
  useGetAuction,
  useUpdateAuction,
} from "@/api/auction/auction";
import {
  appendNoDuplicateSaleNumbersRefine,
  auctionSchema,
} from "@/api/auction/auction-validation";
import * as AF from "@/components/form-items/AbstractForm";
import { ModifiableList } from "@/components/form-items/ModifiableList";
import LinksManagement from "@/components/LinksManagement";
import AddFileModal from "@/components/modal/AddFileModal";
import { MaskableSeparator as Separator } from "@/components/form-items/MaskableSeparator";
import { useAuth } from "@/context/auth/AuthContext";
import { toast } from "@/hooks/use-toast";
import { useDashboardParams } from "@/hooks/useDashboardParams";
import { getMainLanguage } from "@/i18n";
import { FormLayout } from "@/layout/FormLayout";
import { formatFormRegistrationReference } from "@/lib/form-registration-header";
import { computeFieldAuctionSummary } from "@/lib/computeFields";
import { Auction } from "@/lib/types";
import { standardSchemaResolver } from "@hookform/resolvers/standard-schema";
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

const schema = appendNoDuplicateSaleNumbersRefine(auctionSchema.passthrough());

const EditPublicAuction = ({ auction }: { auction: Auction }) => {
  // Rafraîchit le tableau des fichiers après un ajout réussi dans AddFileModal.
  const [refreshFiles, setRefreshFiles] = useState(false);

  const { instanceData } = useAuth();

  const form = useForm({
    defaultValues: auction,
    resolver: standardSchemaResolver(schema),
    reValidateMode: "onSubmit",
  });

  const isModulePhotoEnabled = useMemo(() => {
    return instanceData?.isModulePhotoEnabled;
  }, [instanceData?.isModulePhotoEnabled]);

  const isModuleSculptureEnabled = useMemo(() => {
    return instanceData?.isModuleSculptureEnabled;
  }, [instanceData?.isModuleSculptureEnabled]);

  const { data: attachments } = useGetAttachmentsByDocId(auction.id);

  const auctionUpdater = useUpdateAuction();

  const { t, i18n } = useTranslation();

  const handleResume = (data: any) => {
    return computeFieldAuctionSummary(data, {
      language: getMainLanguage(),
    });
  };

  const { relativePosition } = useDashboardParams<Auction>({
    statusField: "ventesPublicationStatus",
    resourceName: "auction",
    relativePosition: {
      currentId: auction.id,
      positionFetcher: getRelativePosition,
    },
  });

  const queryClient = useQueryClient();

  const [, setSearchParam] = useSearchParams();

  const ventesDescriptionNumeroVente = form.watch("ventesDescriptionNumeroVente");
  const saleLabel = t("backoffice.header.saleNumber.label");
  const registrationReference = useMemo(
    () =>
      formatFormRegistrationReference(ventesDescriptionNumeroVente, {
        label: saleLabel,
        locale: i18n.language,
      }),
    [ventesDescriptionNumeroVente, saleLabel, i18n.language],
  );

  return (
    <FormLayout
      formKey="auction"
      title={auction.ventesDescriptionTitre}
      type="auction"
      relativePosition={relativePosition}
      onSave={(status) =>
        form.handleSubmit(
          (values) => {
            auctionUpdater.mutate(
              {
                ...values,
                ...(status && { ventesPublicationStatus: status }),
              },
              {
                onSuccess: () => {
                  toast({
                    title: t(
                      status === undefined
                        ? "autoSave"
                        : "recordSavedToastTitle",
                    ),
                    variant: "success",
                    description: t("auctionSavedToastDescription"),
                  });
                  form.reset({}, { keepValues: true });
                },
              },
            );
          },
          (invalid) => {
            if (invalid.ventesDescriptionNumeroVente != undefined)
              setSearchParam({ tab: "description" });
          },
        )()
      }
      invalidateOnChange={() => {
        queryClient.invalidateQueries({
          queryKey: ["auction", auction.id],
        });
        queryClient.invalidateQueries({ queryKey: ["auctions"] });

        queryClient.invalidateQueries({
          queryKey: ["attachment", { docId: auction.id }],
        });
      }}
      publicationStatus={auction.ventesPublicationStatus}
      id={auction.id}
      reference={registrationReference}
      picturesData={attachments && { pictures: attachments, type: "auction" }}
      form={form}
    >
      <Separator />
      <AF.AbstractForm
        form={form}
        formKey="auction"
        tabs={
          [
            { nonHideable: true, value: "description", label: t("auctionDescription") },
            { value: "sources", label: t("sources") },
            { value: "files", label: t("fichiers") },
            { value: "links", label: t("linkedEntries") },
          ] as const
        }
      >
        <AF.Tab value="description">
          <AF.Col>
            <AF.Row>
              <AF.Text
                name="ventesDescriptionNumeroVente"
                nonMaskable
                className="w-64"
              />
              <AF.Checkbox name="ventesDescriptionCatalogue" align={true} className="pt-6" />
            </AF.Row>
            <AF.Text name="ventesDescriptionTitre" />
            <AF.Text className="w-64" name="ventesDescriptionDate" />
            <ModifiableList
              name="ventesDescriptionCommissaires"
              listKey="liste-maisons-de-vente"
            />
            <ModifiableList
              name="ventesDescriptionLieu"
              listKey="liste-lieux-de-vente"
            />
            <AF.Text className="w-16" name="ventesDescriptionSalle" />
            <AF.Row>
              <ModifiableList
                name="ventesDescriptionVille"
                listKey="liste-villes"
              />
              <ModifiableList
                name="ventesDescriptionPays"
                listKey="liste-pays"
              />
            </AF.Row>
          </AF.Col>
          <AF.Col>
            <AF.RichText
              name="ventesDescriptionResume"
              computeAction={handleResume}
            />
            <AF.RichText name="ventesDescriptionNotesVente" />
          </AF.Col>
        </AF.Tab>
        <AF.Tab value="sources">
          <AF.RichText name="sources" />
        </AF.Tab>
        <AF.Tab
          isForm={false}
          value="files"
          headerExtras={
            <AddFileModal
              id={auction.id}
              onClose={() => {
                setRefreshFiles(true);
              }}
            />
          }
        >
          <AF.OthersFilesTable
            docId={auction.id}
            name="diversfiles"
            refreshFiles={refreshFiles}
            setRefreshFiles={setRefreshFiles}
          />
        </AF.Tab>
        <AF.Tab value="links">
          <LinksManagement
            data={auction}
            id={auction.id}
            type="auction"
            configuration={{
              fields: [
                {
                  name: "links",
                  type: "links",
                  label: "Liens",
                  links: [
                    {
                      name: "artworkInAuctions",
                      label: t("entryartworks"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNumeroLot",
                          label: t("oeuvresDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionReproduitPage",
                          label: t("oeuvresDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationBasse",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionEstimationHaute",
                          label: t(
                            "oeuvresDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionVendu",
                          label: t("oeuvresDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionResultatNonListe",
                          label: t(
                            "oeuvresDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVenu",
                          label: t("oeuvresDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonVendu",
                          label: t("oeuvresDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNonReproduit",
                          label: t("oeuvresDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionRetire",
                          label: t("oeuvresDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionMiseAPrix",
                          label: t("oeuvresDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixMarteau",
                          label: t("oeuvresDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionPrixAvecFrais",
                          label: t("oeuvresDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "oeuvresDansVentesDescriptionNotes",
                          label: t("oeuvresDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleprintInAuction",
                      label: t("entrymultipleprints"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroVente",
                          ),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResume",
                          ),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNumeroLot",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNumeroLot",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationBasse",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationBasse",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionEstimationHaute",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionEstimationHaute",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionVendu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionVendu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionResultatNonListe",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionResultatNonListe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionNonVenu",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionNonVenu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionRetire",
                          type: "checkbox",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionRetire",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionMiseAPrix",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionMiseAPrix",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixMarteau",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixMarteau",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionPrixAvecFrais",
                          type: "inputSelectCurrency",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionPrixAvecFrais",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "ventesDansEstampesMultiplesDescriptionExemplaireVendu",
                          type: "text",
                          label: t(
                            "auctionDansEstampesMultiplesDescriptionExemplaireVendu",
                          ),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "sculptureEditionInAuction",
                      label: t("linkedSculptureEditions"),
                      type: "link-complex",
                      hidden: !isModuleSculptureEnabled,
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "sculptureEditionDansVentesNumeroLot",
                          type: "text",
                          label: t("sculptureEditionDansVentesNumeroLot"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesReproduitPage",
                          type: "text",
                          label: t("sculptureEditionDansVentesReproduitPage"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesEstimationBasse",
                          type: "inputSelectCurrency",
                          label: t("sculptureEditionDansVentesEstimationBasse"),
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "sculptureEditionDansVentesEstimationHaute",
                          type: "inputSelectCurrency",
                          label: t("sculptureEditionDansVentesEstimationHaute"),
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "sculptureEditionDansVentesVendu",
                          type: "checkbox",
                          label: t("sculptureEditionDansVentesVendu"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesResultatNonListe",
                          type: "checkbox",
                          label: t(
                            "sculptureEditionDansVentesResultatNonListe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesNonVenu",
                          type: "checkbox",
                          label: t("sculptureEditionDansVentesNonVenu"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesNonVendu",
                          type: "checkbox",
                          label: t("sculptureEditionDansVentesNonVendu"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesNonReproduit",
                          type: "checkbox",
                          label: t("sculptureEditionDansVentesNonReproduit"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesRetire",
                          type: "checkbox",
                          label: t("sculptureEditionDansVentesRetire"),
                          className: "w-full",
                        },
                        {
                          name: "sculptureEditionDansVentesMiseAPrix",
                          type: "inputSelectCurrency",
                          label: t("sculptureEditionDansVentesMiseAPrix"),
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "sculptureEditionDansVentesPrixMarteau",
                          type: "inputSelectCurrency",
                          label: t("sculptureEditionDansVentesPrixMarteau"),
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "sculptureEditionDansVentesPrixAvecFrais",
                          type: "inputSelectCurrency",
                          label: t("sculptureEditionDansVentesPrixAvecFrais"),
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "sculptureEditionDansVentesNotes",
                          type: "richtext",
                          label: t("sculptureEditionDansVentesNotes"),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "multipleSampleInAuction",
                      label: t("entrymultiplesamples"),
                      type: "link-complex",
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionNumeroLot",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionNumeroLot",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionReproduitPage",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionReproduitPage",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionEstimationBasse",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionEstimationBasse",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionEstimationHaute",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionEstimationHaute",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionVendu",
                          type: "checkbox",
                          label: t("multiplesampleDansVentesDescriptionVendu"),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionResultatNonListe",
                          type: "checkbox",
                          label: t(
                            "multiplesampleDansVentesDescriptionResultatNonListe",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionNonVenu",
                          type: "checkbox",
                          label: t(
                            "multiplesampleDansVentesDescriptionNonVenu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionNonVendu",
                          type: "checkbox",
                          label: t(
                            "multiplesampleDansVentesDescriptionNonVendu",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionNonReproduit",
                          type: "checkbox",
                          label: t(
                            "multiplesampleDansVentesDescriptionNonReproduit",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionRetire",
                          type: "checkbox",
                          label: t("multiplesampleDansVentesDescriptionRetire"),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionMiseAPrix",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionMiseAPrix",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionPrixMarteau",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionPrixMarteau",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionPrixAvecFrais",
                          type: "text",
                          label: t(
                            "multiplesampleDansVentesDescriptionPrixAvecFrais",
                          ),
                          className: "w-full",
                        },
                        {
                          name: "multiplesampleDansVentesDescriptionNotes",
                          type: "richtext",
                          label: t("multiplesampleDansVentesDescriptionNotes"),
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "tirageInAuction",
                      label: t("entrytirages"),
                      type: "link-complex",
                      hidden: !isModulePhotoEnabled,
                      fields: [
                        {
                          name: "auction.ventesDescriptionNumeroVente",
                          label: t("auctionDescriptionNumeroVente"),
                          type: "text",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "auction.ventesDescriptionResume",
                          label: t("auctionDescriptionResume"),
                          type: "richtext",
                          className: "w-full",
                          disabled: true,
                        },
                        {
                          name: "tirageDansVentesDescriptionNumeroLot",
                          label: t("tirageDansVentesDescriptionNumeroLot"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionReproduitPage",
                          label: t("tirageDansVentesDescriptionReproduitPage"),
                          type: "text",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionEstimationBasse",
                          label: t(
                            "tirageDansVentesDescriptionEstimationBasse",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "tirageDansVentesDescriptionEstimationHaute",
                          label: t(
                            "tirageDansVentesDescriptionEstimationHaute",
                          ),
                          type: "inputSelectCurrency",
                          className: "w-full",
                          defaultSelectValue: "EUR",
                        },
                        {
                          name: "tirageDansVentesDescriptionVendu",
                          label: t("tirageDansVentesDescriptionVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionResultatNonListe",
                          label: t(
                            "tirageDansVentesDescriptionResultatNonListe",
                          ),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonVenu",
                          label: t("tirageDansVentesDescriptionNonVenu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonVendu",
                          label: t("tirageDansVentesDescriptionNonVendu"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNonReproduit",
                          label: t("tirageDansVentesDescriptionNonReproduit"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionRetire",
                          label: t("tirageDansVentesDescriptionRetire"),
                          type: "checkbox",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionMiseAPrix",
                          label: t("tirageDansVentesDescriptionMiseAPrix"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionPrixMarteau",
                          label: t("tirageDansVentesDescriptionPrixMarteau"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionPrixAvecFrais",
                          label: t("tirageDansVentesDescriptionPrixAvecFrais"),
                          type: "inputSelectCurrency",
                          defaultSelectValue: "EUR",
                          className: "w-full",
                        },
                        {
                          name: "tirageDansVentesDescriptionNotes",
                          label: t("tirageDansVentesDescriptionNotes"),
                          type: "richtext",
                          className: "w-full",
                        },
                      ],
                    },
                    {
                      name: "relatedBibliography",
                      label: t("entrybibliographies"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedAudiovisual",
                      label: t("entryaudiovisuals"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedDocument",
                      label: t("entrydocuments"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedWriting",
                      label: t("entrywritings"),
                      type: "link-simple",
                    },
                    {
                      name: "relatedPhotography",
                      label: t("entryphotographies"),
                      type: "link-simple",
                    },
                  ],
                },
              ],
            }}
          />
        </AF.Tab>
      </AF.AbstractForm>
    </FormLayout>
  );
};

export const EditPublicAuctionPage = () => {
  const { id } = useParams();

  const { data, isLoading } = useGetAuction(id);

  return (
    <>
      {data && <EditPublicAuction key={data.id} auction={data} />}
      {!data && !isLoading && <h2 className="p-4">venteNExistePasLabel</h2>}
    </>
  );
};
